import React, { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import Avatar from "@mui/material/Avatar";
import { ReactComponent as Jacob } from "../../../assets/images/ChatImages/albert.svg";
import { ReactComponent as Laptop } from "../../../assets/images/ChatImages/laptop.svg";
import { ListArchivedRoomsApi } from "../../../store/features/ListArchivedRooms";
import { getMessageById } from "../../../store/features/getRoomById";
import { BASE_URL } from "../../../api/ApiConfig";
import { createAppSocket } from '../../../api/createAppSocket';
import Loader from "../../../assets/Loader";
import SearchInput from "../../common/SearchInputField";
import { msgTime } from "../../../utils/index";
import { resolveCustomerIdFromGetMessagePayload } from "../../../utils/messagePayloadCustomer";
import { getOpenRoomCache, setOpenRoomCache } from "../../../utils/openChatRoomCache";
import {
  resolvePayloadRoom,
  resolveRoomSourceGroup,
} from "../../../utils/chatRoomPayload";
import {
  getArchiveChatListWarmCache,
  setArchiveChatListWarmCache,
} from "../../../utils/archiveChatListWarmCache";
import NoChatAvailable from "./NoChatAvailable";
import ChatListSkeleton from "./ChatListSkeleton";
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as Desktop } from "../../../assets/images/ChatImages/laptop.svg";
import { ReactComponent as Mobile } from "../../../assets/images/mobile-devices.svg";
import { getLiveChatBasePath } from "../../../utils/liveChatPath";
import ChatListRowV2 from "../ChatV2/ChatListRowV2";
import {
  getPendingRoomPreview,
  ROOM_PREVIEW_RESOLVED_EVENT,
} from "../../../utils/pendingRoomPreview";

const ENDPOINT = `${BASE_URL}`;

let socket;

/**
 * The archived-rooms endpoint answers either as `{ status, data: { rooms, pagination } }`
 * or as `{ success, rooms, pagination }`, so read both rather than rendering an
 * empty Closed list when the envelope is the one we did not expect.
 */
function readArchivedRoomsPayload(payload) {
  const container =
    payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const rooms = Array.isArray(container?.rooms)
    ? container.rooms
    : Array.isArray(payload?.rooms)
    ? payload.rooms
    : [];
  const pagination = container?.pagination || payload?.pagination || null;
  const ok =
    payload?.status === 200 ||
    payload?.success === true ||
    rooms.length > 0 ||
    !!pagination;
  return { rooms, pagination, ok };
}

/** Reshape an active-list row into a closed-list row for an optimistic insert. */
function toClosedListRow(row) {
  return {
    ...row,
    num: 0,
    isOnline: false,
    isLive: false,
    end_session: true,
    endSession: true,
    is_archived: true,
    is_unAssigned: false,
    // Closed list never shows read-receipt ticks.
    readReceipt: null,
  };
}

/** Sync first paint with session warm cache (ArchiveUser remounts on tab switch). */
function readWarmArchiveListSeed(businessId) {
  const warmCache = getArchiveChatListWarmCache();
  const warm =
    Boolean(businessId) &&
    Array.isArray(warmCache?.data) &&
    warmCache.data.length > 0 &&
    warmCache.businessId === businessId;
  if (!warm) {
    return { data: [], isLoading: true, hasMore: true };
  }
  return {
    data: warmCache.data,
    isLoading: false,
    hasMore: warmCache.hasMore !== false,
  };
}

function ArchiveUser(props) {
  const {
    selectUser,
    setSelectUser,
    setGetRoomId,
    getRoomId,
    refreshChatList,
    setChatSideBar,
    fromSearch = false,
    urlRoomId = "",
    setRoomMessagesLoading,
    variant,
    setClosedCount,
    closedRoomSignal,
  } = props;
  const isV2 = variant === "v2";
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.loginToken);
  const agentToken = useSelector((state) => state.loginTokenCoUser);
  const userData = useSelector((state) => state.loginToken);
  const agentData = useSelector((state) => state.loginTokenCoUser);
  const businessId = userData?.response?.user?.business_id;

  const [activeChat, setActiveChat] = useState(null);
  const [chatMessage, setChatMessage] = useState("");

  const [data, setData] = useState(() => readWarmArchiveListSeed(businessId).data);
  const [isLoading, setIsLoading] = useState(() => readWarmArchiveListSeed(businessId).isLoading);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(() => readWarmArchiveListSeed(businessId).hasMore);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(hasMore);
  const isFetchingMoreRef = useRef(false);
  const getRoomListRef = useRef(null);
  pageRef.current = page;
  hasMoreRef.current = hasMore;
  const [activation, setActivation] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const socketRef = useRef(null);
  const listRef = useRef(null);
  /** Rooms closed locally that the archived-rooms endpoint has not returned yet. */
  const pendingClosedRowsRef = useRef(new Map());
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appliedUrlRoomRef = useRef(null);
  const hydratedUrlRoomRef = useRef(null);
  const selectUserRef = useRef(selectUser);
  selectUserRef.current = selectUser;
  /** When opening from the archive list, click + URL sync would both call RoomByIdHandler; skip the duplicate. */
  const skipDuplicateUrlRoomFetchRef = useRef(null);

  const formatStartTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);
    const hours = date.getHours();
    const minutes = `0${date.getMinutes()}`.slice(-2);
    const amOrPm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    return `${year}-${month}-${day} ${formattedHours}:${minutes} ${amOrPm}`;
  };
  const chracterPosition = (alphabet) => {
    const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const positionOfAlphabet = alphabets.indexOf(alphabet) + 1;
    return positionOfAlphabet;
  };

  const colorCodesForAvatar = [
    "#405261",
    "#FF4D4F",
    "#FA541C",
    "#FA8C16",
    "#FAAD14",
    "#EB2F96",
    "#FADB14",
    "#A0D911",
    "#52C41A",
    "#5CDBD3",
    "#9254DE",
    "#F759AB",
    "#2F54EB",
    "#722ED1",
    "#1890FF",
    "#13C2C2",
    "#2DB7F5",
    "#19BE6B",
    "#30A745",
    "#FA8C16",
    "#FAAD14",
    "#9254DE",
    "#F759AB",
    "#13C2C2",
    "#722ED1",
    "#2DB7F5",
  ];

  useEffect(() => {
    socket = createAppSocket();
    socket.on("create-room", handleRoomCreated);
    return () => {
      socket.disconnect();
    };
  }, [refreshChatList]);


  useEffect(() => {
    if (fromSearch && urlRoomId) {
      return;
    }
    const bid = userData?.response?.user?.business_id;
    const warmCache = getArchiveChatListWarmCache();
    const warm =
      Array.isArray(warmCache?.data) &&
      warmCache.data.length > 0 &&
      warmCache.businessId === bid;
    if (warm) {
      setData(warmCache.data);
      setHasMore(warmCache.hasMore);
      setPage(1);
      setIsLoading(false);
      return;
    }
    getRoomList(1);
  }, [fromSearch, userData?.response?.user?.business_id]);

  useEffect(() => {
    if (fromSearch && urlRoomId) {
      setHasMore(false);
      setPage(1);
      setData([]);
      setIsLoading(true);
    }
  }, [fromSearch, urlRoomId]);

  // Open room immediately from URL roomId without scanning paginated archived list.
  useEffect(() => {
    const roomIdFromUrl = searchParams.get("roomId");
    if (!roomIdFromUrl) return;

    if (skipDuplicateUrlRoomFetchRef.current === roomIdFromUrl) {
      skipDuplicateUrlRoomFetchRef.current = null;
      appliedUrlRoomRef.current = roomIdFromUrl;
      return;
    }

    if (appliedUrlRoomRef.current === roomIdFromUrl) return;

    appliedUrlRoomRef.current = roomIdFromUrl;
    setChatSideBar(true);
    handleChatClick(roomIdFromUrl);

    // The chat the agent is reading just moved into Closed: its messages are
    // already on screen, so refetching them would only flash a skeleton.
    const alreadyOpenWithMessages =
      selectUser &&
      String(selectUser.id) === String(roomIdFromUrl) &&
      Array.isArray(getRoomId) &&
      getRoomId.length > 0;
    if (alreadyOpenWithMessages) {
      hydratedUrlRoomRef.current = roomIdFromUrl;
      setSelectUser((prev) =>
        prev && String(prev.id) === String(roomIdFromUrl)
          ? { ...prev, is_archived: true }
          : prev
      );
      return;
    }

    const roomFromLoadedList = data.find((item) => item.id === roomIdFromUrl);
    if (roomFromLoadedList) {
      handleUserClick(roomFromLoadedList);
    } else {
      // A chat that just closed while open is already selected; keep its details
      // instead of blanking the header behind a placeholder.
      setSelectUser((prev) =>
        prev && String(prev.id) === String(roomIdFromUrl)
          ? { ...prev, is_archived: true }
          : {
              id: roomIdFromUrl,
              title: "Loading...",
              time: "",
              is_archived: true,
              businessId: userData?.response?.user?.business_id,
              customerId: null,
              roomUsers: [],
            }
      );
    }

    RoomByIdHandler(roomIdFromUrl);
  }, [searchParams, data]);

  // Once paginated list contains the selected URL room, hydrate UI with full room info.
  useEffect(() => {
    const roomIdFromUrl = searchParams.get("roomId");
    if (!roomIdFromUrl) return;
    if (hydratedUrlRoomRef.current === roomIdFromUrl) return;
    const roomFromLoadedList = data.find((item) => item.id === roomIdFromUrl);
    if (!roomFromLoadedList) return;
    hydratedUrlRoomRef.current = roomIdFromUrl;
    handleUserClick(roomFromLoadedList);
  }, [searchParams, data]);

  // Chat V2: when landing with no selection (and no deep-link roomId), open the top chat.
  useEffect(() => {
    if (!isV2 || fromSearch || isLoading) return;
    // Only while Closed is the active tab — avoid racing other tabs during switch
    // (e.g. leaving Closed for All briefly kept this list mounted with no roomId).
    const tab = (searchParams.get("tab") || "").toLowerCase();
    if (tab !== "closed" && tab !== "archive") return;
    if (searchParams.get("roomId")) return;
    if (selectUserRef.current) return;
    if (!data?.length) return;

    const top = data[0];
    if (!top?.id) return;

    navigate(
      `${getLiveChatBasePath(window.location.pathname)}?roomId=${top.id}&tab=closed`,
      { replace: true }
    );
  }, [isV2, fromSearch, isLoading, searchParams, data, navigate]);

  const handleRoomCreated = (newRoom) => {
    console.log("Room created:", newRoom);
    if (!newRoom) return;
    setData((prevData) => [...prevData, newRoom]);
    // Optionally refresh the list or just append
    // getRoomList(1); // Calling this resets the list, which might not be desired for simple updates
  };

  const getRoomList = async (pageNum = 1, { background = false } = {}) => {
    if (pageNum === 1 && !background) {
      setIsLoading(true);
    }

    const outputarray = [];
    const listCoUserList = dispatch(
      ListArchivedRoomsApi({
        mainUserId: userData?.response?.user?._id,
        business_id: userData?.response?.user?.business_id,
        page: pageNum,
        limit: 10
      })
    );

    try {
      const data = await listCoUserList;

      if (!data.payload) {
        setIsLoading(false);
        toast.error("Some Error Occurred. Please try again later.");
        return;
      }

      const { rooms: fetchedRooms, pagination: fetchedPagination, ok } =
        readArchivedRoomsPayload(data.payload);

      if (ok) {
        const rooms = fetchedRooms;
        rooms.forEach((item) => {
          console.log("item in archive", item);
          const users = Array.isArray(item?.users) ? item.users : [];
          const loggedInAgentId = userData?.response?.user?._id;
          const customerUser =
            users.find((user) => user?._id && user?._id !== loggedInAgentId && user?.is_guest) ||
            users.find((user) => user?._id && user?._id !== loggedInAgentId) ||
            users[0] ||
            null;
          const date = new Date(item.createdAt);
          const year = date.getFullYear();
          const month = `0${date.getMonth() + 1}`.slice(-2);
          const day = `0${date.getDate()}`.slice(-2);
          const hours = `0${date.getHours()}`.slice(-2);
          const minutes = `0${date.getMinutes()}`.slice(-2);
          const amOrPm = hours >= 12 ? "PM" : "AM";
          const formattedHours = hours % 12 || 12;
          const formattedTime = `${year}-${month}-${day} ${formattedHours}:${minutes} ${amOrPm}`;
          // A snapshot can be present but empty, so fall through to the room's
          // latest message instead of stopping at the snapshot.
          const apiDescription =
            item?.latestMessageSnapshot?.content || item?.latestMessage?.content;
          const apiAttachment =
            item?.latestMessageSnapshot?.file_image || item?.latestMessage?.file_image;
          // A chat closed before the visitor replied can be listed with no latest
          // message at all, even though the welcome message is sitting in the
          // thread. Opening it publishes that text, so reuse it here.
          const pendingPreview =
            apiDescription || apiAttachment ? null : getPendingRoomPreview(item._id);

          outputarray.push({
            id: item._id,
            name: userData?.response?.user?.full_name,
            title: item?.roomName,
            logo: <Jacob />,
            description: apiDescription || pendingPreview?.description || "",
            attachment: apiAttachment || pendingPreview?.attachment || false,
            time: msgTime(
              item?.latestMessageSnapshot?.createdAt ||
                item?.latestMessage?.createdAt ||
                pendingPreview?.iso
            ),
            iso:
              item?.latestMessageSnapshot?.createdAt ||
              item?.latestMessage?.createdAt ||
              pendingPreview?.iso ||
              null,
            startTime: formattedTime,
            text: "Windows",
            deviceType: customerUser?.deviceType,
            os: customerUser?.os,
            McLogo: <Laptop />,
            // Closed/archived chats should not show unread badges
            num: 0,
            isOnline: false,
            rating: item?.feedback?.rating,
            isIssueResolved: item?.feedback?.issueResolved,
            endSession: item?.end_session,
            end_session: !!item?.end_session,
            endSessionTime: item?.end_session_time,
            is_archived: true,
            roomUsers: users.map((user) => user?._id).filter(Boolean),
            customerId: customerUser?._id || null,
            archived_message: item?.archived_message || item?.endedMessage,
            tags: item?.tags || [],
            readReceipt: null,
          });
        });

        let nextHasMore = rooms.length === 10;
        const pagination = fetchedPagination;
        if (pagination) {
          const { page: p, totalPages } = pagination;
          nextHasMore = p < totalPages;
        }
        const listTotal =
          pagination?.total ?? (pageNum === 1 ? outputarray.length : undefined);

        if (pageNum === 1) {
          // Keep rooms closed in this session on top until the API returns them,
          // otherwise a refetch would drop them back out of the Closed list.
          const fetchedIds = new Set(outputarray.map((room) => String(room.id)));
          pendingClosedRowsRef.current.forEach((_row, roomId) => {
            if (fetchedIds.has(roomId)) pendingClosedRowsRef.current.delete(roomId);
          });
          const pendingRows = Array.from(pendingClosedRowsRef.current.values());
          const nextData = [...pendingRows, ...outputarray];

          setData(nextData);
          const bid = userData?.response?.user?.business_id;
          if (bid) {
            setArchiveChatListWarmCache({
              businessId: bid,
              data: nextData,
              hasMore: nextHasMore,
              page: 1,
              ...(typeof listTotal === "number" ? { total: listTotal } : {}),
            });
          }
          if (typeof listTotal === "number" && typeof setClosedCount === "function") {
            setClosedCount(listTotal + pendingRows.length);
          }
        } else {
          setData((prev) => [...prev, ...outputarray]);
        }

        setHasMore(nextHasMore);

        setIsLoading(false);
      } else if (data.payload.status === 500) {
        setIsLoading(false);
        toast.error("Some Error Occurred. Please try again later.");
      } else if (data.payload.status === 401) {
        setIsLoading(false);
        toast.error("Login Expired. Please login and try again.");
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(error);
    }
  };

  getRoomListRef.current = getRoomList;

  // A chat closed while this tab is open must show up here straight away.
  const appliedClosedSignalRef = useRef(null);
  useEffect(() => {
    const signalKey = closedRoomSignal
      ? `${closedRoomSignal.roomId}-${closedRoomSignal.at}`
      : null;
    if (!signalKey || appliedClosedSignalRef.current === signalKey) return;
    appliedClosedSignalRef.current = signalKey;

    const { roomId, row } = closedRoomSignal;
    if (data.some((item) => String(item.id) === String(roomId))) return;

    if (row) {
      const closedRow = toClosedListRow(row);
      pendingClosedRowsRef.current.set(String(roomId), closedRow);
      setData((prev) => [closedRow, ...prev]);
    }

    setPage(1);
    void getRoomListRef.current?.(1, { background: true });
  }, [closedRoomSignal, data]);

  // Opening a closed chat reads its thread, which is the only place the welcome
  // message lives when the room was closed before the visitor replied. Fill the
  // row that had to render blank rather than leaving it empty.
  useEffect(() => {
    const onPreviewResolved = (event) => {
      const { roomId, description, attachment } = event?.detail || {};
      if (!roomId) return;

      setData((prevData) => {
        let changed = false;
        const next = (prevData || []).map((room) => {
          if (String(room?.id) !== String(roomId)) return room;
          const alreadyHasPreview =
            Boolean(String(room?.description || "").trim()) || Boolean(room?.attachment);
          if (alreadyHasPreview) return room;
          changed = true;
          return {
            ...room,
            description: description || "",
            attachment: Boolean(attachment),
          };
        });
        return changed ? next : prevData;
      });
    };

    window.addEventListener(ROOM_PREVIEW_RESOLVED_EVENT, onPreviewResolved);
    return () =>
      window.removeEventListener(ROOM_PREVIEW_RESOLVED_EVENT, onPreviewResolved);
  }, []);

  const loadMore = useCallback(() => {
    if (isFetchingMoreRef.current || !hasMoreRef.current) return;
    isFetchingMoreRef.current = true;
    const nextPage = pageRef.current + 1;
    setPage(nextPage);
    pageRef.current = nextPage;
    void Promise.resolve(getRoomListRef.current?.(nextPage)).finally(() => {
      isFetchingMoreRef.current = false;
    });
  }, []);

  const RoomByIdHandler = async (rowId) => {
    if (
      activeChat === rowId &&
      Array.isArray(getRoomId) &&
      getRoomId.length > 0
    ) {
      return;
    }

    const cached = getOpenRoomCache(rowId, true);
    if (cached != null && Array.isArray(cached.messages)) {
      const listRow = data.find((item) => item.id === rowId);
      const merge = cached.selectUserMerge || {};
      const ended = !!(
        merge.end_session ??
        listRow?.end_session ??
        listRow?.endSession
      );
      const endSessionTimeIso =
        merge.endSessionTime ||
        merge.endSessionTimeIso ||
        listRow?.endSessionTime ||
        null;

      socket.emit("join chat", rowId);
      setGetRoomId(cached.messages);
      setSelectUser((prev) => ({
        ...(listRow || prev || {}),
        ...merge,
        id: rowId,
        is_archived: true,
        end_session: ended,
        endSession: ended,
        endSessionTime: endSessionTimeIso,
        endSessionTimeIso: endSessionTimeIso,
        rating: listRow?.rating ?? merge.rating ?? prev?.rating,
        isIssueResolved:
          listRow?.isIssueResolved ?? merge.isIssueResolved ?? prev?.isIssueResolved,
        archived_message:
          merge.archived_message ??
          listRow?.archived_message ??
          prev?.archived_message,
        time: merge.time ?? listRow?.time ?? prev?.time,
        startTime: merge.startTime ?? listRow?.startTime ?? prev?.startTime,
        createdAtIso:
          merge.createdAtIso ??
          listRow?.createdAtIso ??
          prev?.createdAtIso,
      }));
      setRoomMessagesLoading?.(false);
      return;
    }

    setGetRoomId([]);
    setRoomMessagesLoading?.(true);
    const listCoUserList = dispatch(
      getMessageById({
        id: rowId,
      })
    );
    listCoUserList
      .then(async (data) => {
        if (typeof data.payload === "undefined") {
          toast.error("Some Error Occured. Please try again later.");
          if (fromSearch && urlRoomId === rowId) setIsLoading(false);
        } else if (data?.payload?.status === 200) {
          socket.emit("join chat", rowId);
          const messages = Array.isArray(data?.payload?.messages) ? data.payload.messages : [];
          setGetRoomId(messages);

          // Enrich selected user from room-by-id payload so header/sidebar don't wait for paginated list.
          const payloadRoom = resolvePayloadRoom(data.payload);
          const roomSourceGroup = resolveRoomSourceGroup(payloadRoom);
          const payloadUsers = Array.isArray(payloadRoom?.users) ? payloadRoom.users : [];
          const roomUsers = payloadUsers.map((user) => user?._id).filter(Boolean);
          const loggedInAgentId = userData?.response?.user?._id;
          const visitorUser =
            payloadUsers.find((user) => user?._id && user?._id !== loggedInAgentId && user?.is_guest) ||
            payloadUsers.find((user) => user?._id && user?._id !== loggedInAgentId) ||
            payloadUsers[0] ||
            null;
          const latestMessage = messages[messages.length - 1];
          const visitorMessage = [...messages].reverse().find((m) => !m?.isAgent) || null;

          const customerIdResolved = resolveCustomerIdFromGetMessagePayload(data.payload, {
            loggedInAgentId,
            payloadUsers,
            visitorMessage,
          });
          const fallbackTitle =
            payloadRoom?.roomName ||
            data.payload?.roomName ||
            visitorMessage?.sender_name ||
            latestMessage?.sender_name ||
            "Visitor";
          const latestTime =
            latestMessage?.createdAt ||
            payloadRoom?.latestMessageSnapshot?.createdAt ||
            payloadRoom?.updatedAt ||
            payloadRoom?.createdAt ||
            "";

          const createdAtIso =
            payloadRoom?.createdAt || latestTime || null;
          const endSessionTimeIso =
            payloadRoom?.end_session_time ||
            data.payload?.end_session_time ||
            null;
          const ended =
            typeof payloadRoom?.end_session === "boolean"
              ? payloadRoom.end_session
              : typeof data.payload?.end_session === "boolean"
                ? data.payload.end_session
                : false;
          const archivedMessage =
            payloadRoom?.archived_message ||
            payloadRoom?.endedMessage ||
            data.payload?.archived_message ||
            data.payload?.endedMessage ||
            null;

          const selectUserMerge = {
            title: fallbackTitle,
            time: latestTime ? msgTime(latestTime) : "",
            startTime: formatStartTime(createdAtIso) || "",
            createdAtIso,
            deviceType: visitorUser?.deviceType,
            os: visitorUser?.os,
            roomUsers: roomUsers.length ? roomUsers : customerIdResolved ? [customerIdResolved] : [],
            customerId: customerIdResolved || null,
            businessId: payloadRoom?.business_id || userData?.response?.user?.business_id,
            assignedTo: payloadRoom?.assigned_to,
            tags: payloadRoom?.tags || [],
            end_session: ended,
            endSession: ended,
            endSessionTime: endSessionTimeIso,
            endSessionTimeIso,
            rating: payloadRoom?.feedback?.rating ?? data.payload?.feedback?.rating,
            isIssueResolved:
              payloadRoom?.feedback?.issueResolved ??
              data.payload?.feedback?.issueResolved,
            archived_message: archivedMessage,
            sourceGroup: roomSourceGroup,
          };

          setSelectUser((prev) => ({
            ...(prev || {}),
            id: rowId,
            title: prev?.title && prev.title !== "Loading..." ? prev.title : fallbackTitle,
            time: latestTime ? msgTime(latestTime) : (prev?.time || ""),
            startTime:
              formatStartTime(payloadRoom?.createdAt || latestTime) ||
              prev?.startTime ||
              "",
            deviceType: visitorUser?.deviceType || prev?.deviceType,
            os: visitorUser?.os || prev?.os,
            roomUsers:
              roomUsers.length
                ? roomUsers
                : (customerIdResolved ? [customerIdResolved] : (prev?.roomUsers || [])),
            customerId: customerIdResolved || prev?.customerId || null,
            businessId:
              payloadRoom?.business_id ||
              userData?.response?.user?.business_id ||
              prev?.businessId,
            assignedTo: payloadRoom?.assigned_to || prev?.assignedTo,
            tags: payloadRoom?.tags || prev?.tags || [],
            end_session: ended,
            endSession: ended,
            endSessionTime: endSessionTimeIso || prev?.endSessionTime,
            endSessionTimeIso: endSessionTimeIso || prev?.endSessionTimeIso,
            rating:
              payloadRoom?.feedback?.rating ??
              data.payload?.feedback?.rating ??
              prev?.rating,
            isIssueResolved:
              payloadRoom?.feedback?.issueResolved ??
              data.payload?.feedback?.issueResolved ??
              prev?.isIssueResolved,
            archived_message: archivedMessage || prev?.archived_message,
            createdAtIso: createdAtIso || prev?.createdAtIso,
            sourceGroup: roomSourceGroup,
            is_archived: true,
          }));

          setOpenRoomCache(rowId, true, {
            messages,
            sourceGroup: roomSourceGroup,
            selectUserMerge,
          });

          const fsOpen =
            searchParams.get("fromSearch") === "1" ||
            searchParams.get("fromSearch") === "true";
          const ridOpen = searchParams.get("roomId");
          if (fsOpen && ridOpen === rowId) {
            setData([
              {
                id: rowId,
                name: userData?.response?.user?.full_name,
                title: fallbackTitle,
                logo: <Jacob />,
                description:
                  latestMessage?.content || visitorMessage?.content || "",
                attachment:
                  latestMessage?.file_image || visitorMessage?.file_image,
                time: latestTime ? msgTime(latestTime) : " ",
                startTime:
                  formatStartTime(payloadRoom?.createdAt || latestTime) || "",
                text: "Windows",
                deviceType: visitorUser?.deviceType,
                os: visitorUser?.os,
                McLogo: <Laptop />,
                num: 0,
                isOnline: false,
                rating: data.payload?.feedback?.rating ?? payloadRoom?.feedback?.rating,
                isIssueResolved:
                  data.payload?.feedback?.issueResolved ??
                  payloadRoom?.feedback?.issueResolved,
                endSession: payloadRoom?.end_session,
                endSessionTime: payloadRoom?.end_session_time,
                roomUsers:
                  roomUsers.length
                    ? roomUsers
                    : customerIdResolved
                    ? [customerIdResolved]
                    : [],
                customerId: customerIdResolved || null,
                archived_message: archivedMessage,
                tags: payloadRoom?.tags || [],
                readReceipt: null,
              },
            ]);
          }
          if (fromSearch && urlRoomId === rowId) {
            setIsLoading(false);
          }
        } else if (data?.payload?.status === 500) {
          toast.error("Some Error Occured. Please try again later.");
          if (fromSearch && urlRoomId === rowId) setIsLoading(false);
        } else if (data?.payload?.status === 401) {
          toast.error("Login Expired. Please login  and try again.");
          if (fromSearch && urlRoomId === rowId) setIsLoading(false);
        }
      })
      .catch(async (error) => {
        toast.error(error);
        if (fromSearch && urlRoomId === rowId) setIsLoading(false);
      })
      .finally(() => {
        setRoomMessagesLoading?.(false);
      });
  };

  const handleChatClick = (id) => {
    setActiveChat(id);
  };

  const handleUserClick = (user) => {
    if (activeChat === user.id) return;

    const ended = !!(user.end_session ?? user.endSession);
    const updatedUser = {
      ...user,
      is_archived: true,
      end_session: ended,
      endSession: user.endSession ?? user.end_session ?? ended,
    };

    setSelectUser(updatedUser);
    handleChatClick(user.id);
    setChatSideBar(true);
  };

  const filteredData = data.filter((item) =>
    item?.title?.toLowerCase().includes((searchTerm || "").toLowerCase())
  );

  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }) => {
      if (!hasMoreRef.current || isFetchingMoreRef.current) return;
      const n = filteredData.length;
      if (n === 0) return;
      if (visibleStopIndex >= n - 1) {
        loadMore();
      }
    },
    [filteredData.length, loadMore]
  );

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Add effect to handle data changes for FixedSizeList
  useEffect(() => {
    // FixedSizeList doesn't have resetAfterIndex method
    // Instead, we'll rely on React's key prop mechanism to force remounting when data changes
  }, [data, searchTerm]);

  const searchSingleOpen =
    searchParams.get("fromSearch") === "1" ||
    searchParams.get("fromSearch") === "true";
  const searchRoomIdParam = searchParams.get("roomId");
  const showSearchSkeleton =
    searchSingleOpen &&
    searchRoomIdParam &&
    isLoading &&
    (!data || data.length === 0);

  if (showSearchSkeleton) {
    return <ChatListSkeleton />;
  }

  if (!isLoading && (!data || data.length === 0)) {
    return <NoChatAvailable message="No archived chats available yet" />
  }

  if (isLoading) {
    return <ChatListSkeleton />;
  }

  console.log("selectUser in archive", );

  const ArchiveChatItem = ({ index, style }) => {
    const item = filteredData[index];
    const isActive = activeChat === item.id;

    const activate = () => {
      if (
        activeChat === item.id &&
        Array.isArray(getRoomId) &&
        getRoomId.length > 0
      ) {
        return;
      }
      skipDuplicateUrlRoomFetchRef.current = item.id;
      navigate(
        `${getLiveChatBasePath(window.location.pathname)}?roomId=${item.id}&tab=archive`
      );
      handleChatClick(item.id);
      handleUserClick(item);
      RoomByIdHandler(item.id);
    };

    if (isV2) {
      return (
        <ChatListRowV2
          style={style}
          item={item}
          isActive={isActive}
          showTyping={false}
          onClick={activate}
        />
      );
    }

    return (
      <div
        style={{
          ...style,
          height: 'auto',
          transform: 'none',
          willChange: 'transform',
          position: 'absolute',
          top: style.top,
          left: style.left,
          right: style.right,
          marginTop: '10px'
        }}
      >
        <div
          className={`ChatUser-Container ${isActive ? "ChatUser-ContainerActive" : ""
            }`}
          key={item.id}
          onClick={activate}
        >
          <p>
            {item.img ? (
              <Avatar
                src={item.img}
                alt="users logo"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                }}
              />
            ) : (
              <Avatar
                sx={{
                  bgcolor:
                    colorCodesForAvatar[
                    chracterPosition(
                      item.title?.substring(0, 1).toUpperCase()
                    )
                    ],
                }}
              >
                {item.title?.substring(0, 1).toUpperCase()}
              </Avatar>
            )}
          </p>
          <div className="Chat-User-Main">
            <div className="Chat-User00">
              <div className="Chat-User-Kathryn">
                <p className="ChatUser-text"> {item.title}</p>
              </div>
              <p className="ChatUser-time"> {item.time.toLowerCase()}</p>
            </div>
            <div className="Chat-User02">
              <p className="ChatUser-discreption">{item.description}</p>
              {/* Closed/archive list does not show read-receipt ticks */}
              {/* <p className="ChatUser-red">{item.num}</p> */}
            </div>
            <div className="Chat-User03">
              {item.deviceType === "desktop" ? <Desktop /> : <Mobile />}
              <p className="ActiveUser-Max">{item?.os}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {!isV2 && (
        <div className="ChatBar-SearchInput">
          <SearchInput
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
            title=""
          />
        </div>
      )}
      <div className="ChatUser-Main-Container" style={{ height: isV2 ? '100%' : 'calc(100vh - 15em)', overflow: 'hidden', padding: 0 }}>
        <div style={{ height: '100%', width: '100%' }}>
          <AutoSizer>
            {({ height, width }) => (
              <List
                ref={listRef}
                height={height || 500}
                width={width || 300}
                itemCount={filteredData.length}
                itemSize={isV2 ? 104 : 90}
                overscanCount={10}
                onItemsRendered={handleItemsRendered}
                className="virtualized-chat-list"
                key={`archive-list-${searchTerm}`}
              >
                {ArchiveChatItem}
              </List>
            )}
          </AutoSizer>
        </div>
      </div>
    </>
  );
}

export default ArchiveUser;
